import { PlusIcon } from "@heroicons/react/24/outline";
import { EPaymentMethod } from "common/enums/ProductSubscribed/EPaymentMethod";
import { ESubscriptionProductType } from "common/enums/ProductSubscribed/ESubscriptionProductType";
import ProductSubscriptionCreateResource from "common/resources/ProductSubscribed/ProductSubscriptionCreateResource";
import Button, { EButtonVariant } from "components/elements/Button";
import Modal from "components/elements/Modal";
import Table from "components/elements/Table";
import { ECellType, IRowProps, TableColumn } from "components/elements/Table/MuiTable";
import Typography, { ITypo } from "components/elements/Typography";
import InputElement, { EInputType } from "components/materials/Form/InputElement";
import MuiSelectInputElement, { IMuiSelectOption } from "components/materials/Form/MuiSelectInputElement";
import I18n from "components/materials/I18n";
import useOpenable from "hooks/useOpenable";
import { useCallback, useEffect, useMemo, useState } from "react";
import ProductService from "services/ProductService";
import { container } from "tsyringe";
import DemembrementConfigTable from "components/pages/Subscriptions/elements/DemembrementConfigTable";
import DemembrementWarnings from "components/pages/Subscriptions/elements/DemembrementWarnings";
import { EDemembrementType } from "common/enums/ProductSubscribed/EDemembrementType";
import { getViagerRatio } from "utils/baremeViager";
import classes from "./classes.module.scss";
import { ProductClasses, TProductName } from "common/resources/Scpi/PhysicalPerson/Products";
import ProductSheetResponseResource from "common/resources/ProductSheet/ProductSheetResponseResource";
import { useNavigate } from "react-router-dom";
import ClientSheetResponseResource from "common/resources/ClientSheet/ClientSheetResponseResource";
import ClientApi from "api/wenImmo/ClientApi";
import { EAvailableProducts } from "common/enums/Scpi/EAvailableProduts";
import { availablePaymentMethods, isConsortiumAffiliated } from "common/utils/consortiumCredit";
import ConsortiumService from "services/ConsortiumService";

const clientApi = container.resolve(ClientApi);
const consortiumService = container.resolve(ConsortiumService);

export type IProductSelected = {
	product: ProductSheetResponseResource;
	dto: ProductSubscriptionCreateResource;
};

type IProps = {
	productsInSubscription: IProductSelected[];
	setProductsInSubscription: (products: IProductSelected[]) => void;
	subscriberId: string;
};

const productService = container.resolve(ProductService);

function calculateAge(birthDate: Date, currentDate: Date): number {
	let age = currentDate.getFullYear() - birthDate.getFullYear();

	// Vérifie si l'anniversaire est déjà passé cette année
	if (currentDate.getMonth() < birthDate.getMonth() || (currentDate.getMonth() === birthDate.getMonth() && currentDate.getDate() < birthDate.getDate())) {
		age--;
	}

	return age;
}

export default function ProductSelectorTable({ productsInSubscription, setProductsInSubscription, subscriberId }: IProps) {
	const [amountSelected, setAmountSelected] = useState<number>(1);
	const [productSelected, setProductSelected] = useState<ProductSheetResponseResource | null>(null);
	const [paymentMethodSelected, setPaymentMethodSelected] = useState<EPaymentMethod | null>(null);
	const [paymentMethodHasError, setPaymentMethodHasError] = useState<boolean>(false);
	const [subscriptionProductTypeSelected, setSubscriptionProductTypeSelected] = useState<ESubscriptionProductType>(ESubscriptionProductType.pleine_propriete);
	const [demembrementDuration, setDemembrementDuration] = useState<number | null>(null);
	const [demembrementType, setDemembrementType] = useState<EDemembrementType>(EDemembrementType.temporaire);
	const [viagerAge, setViagerAge] = useState<number | null>(null);
	const [contrepartieChoice, setContrepartieChoice] = useState<"avec" | "sans" | null>(null);
	const [contrepartieNom, setContrepartieNom] = useState<string | null>(null);
	const [products, setProducts] = useState<ProductSheetResponseResource[]>([]);
	const [userToSubscribe, setUserToSubscribe] = useState<ClientSheetResponseResource | null>(null);
	const [productsAvailable, setProductsAvailable] = useState<ProductSheetResponseResource[]>([]);
	const [isConsortiumMember, setIsConsortiumMember] = useState(false);
	const { isOpen, close, open } = useOpenable();

	const { isOpen: is85YearsModalOpened, close: close85YearsModal, open: open85YearsModal } = useOpenable();
	const navigate = useNavigate();

	const lockedType = useMemo(() => productsInSubscription[0]?.dto.subscriptionProductType ?? null, [productsInSubscription]);

	useEffect(() => {
		if (lockedType) setSubscriptionProductTypeSelected(lockedType);
	}, [lockedType]);

	useEffect(() => {
		clientApi.getClientSheetById(subscriberId).then((client) => {
			setUserToSubscribe(client);
			setViagerAge(null);
		});
	}, [subscriberId]);

	useEffect(() => {
		productService.getLastProductSheets().then(setProducts);
	}, []);

	useEffect(() => {
		let active = true;
		consortiumService.getPage().then((page) => {
			if (active) setIsConsortiumMember(isConsortiumAffiliated(page.registration?.status));
		}).catch(() => {
			if (active) setIsConsortiumMember(false);
		});
		return () => { active = false; };
	}, []);

	useEffect(() => {
		setAmountSelected(productSelected?.minNumberOfShares ?? 1);
		setDemembrementDuration(null);
		setContrepartieChoice(null);
		setContrepartieNom(null);
	}, [productSelected?.minNumberOfShares]);

	useEffect(() => {
		const needsDemembrement = subscriptionProductTypeSelected !== ESubscriptionProductType.pleine_propriete;
		const productsFiltered = products.filter((product) => {
			const alreadyInSubscription = productsInSubscription.find((p) => p.product.id === product.id);
			if (alreadyInSubscription) return false;
			if (needsDemembrement) return (product.demembrementKeys?.length ?? 0) > 0;
			return true;
		});
		setProductsAvailable(productsFiltered);
		setProductSelected(null);
	}, [products, productsInSubscription, subscriptionProductTypeSelected]);

	const redirectToHome = () => {
		close85YearsModal();
		navigate("/");
	};

	const subscriptionProductTypeOptions: IMuiSelectOption[] = useMemo(
		() =>
			Object.values(ESubscriptionProductType).map((type) => ({
				id: type,
				label: I18n.asset.enums.ESubscriptionProductType[type],
			})),
		[],
	);

	const productSelectorColumsHead: TableColumn[] = useMemo(
		() => [
			{
				headContent: I18n.asset.pages.subscriptions.select_products.table.columns.subscription_product_type,
				key: "subscriptionProductType",
				widthPercentage: 15,
			},
			{
				headContent: I18n.asset.pages.subscriptions.select_products.table.columns.scpi,
				key: "name",
				widthPercentage: 20,
			},
			{
				headContent: I18n.asset.pages.subscriptions.select_products.table.columns.paiement_method,
				key: "paymentMethod",
			},
			{
				headContent: I18n.asset.pages.subscriptions.select_products.table.columns.start_price,
				formatType: ECellType.EURO,
				key: "price",
			},
			{
				headContent: I18n.asset.pages.subscriptions.select_products.table.columns.amount,
				key: "amount",
				widthPercentage: 10,
			},
			{
				headContent: I18n.asset.pages.subscriptions.select_products.table.columns.calculated_price,
				formatType: ECellType.EURO,
				key: "calculatedPrice",
			},
			{
				headContent: "",
				key: "addToCart",
				widthPercentage: 10,
			},
		],
		[],
	);

	const paymentMethods = useMemo(() => {
		if (!productSelected) return [];
		const paymentMethods = ProductClasses[productSelected.product?.referenceName as TProductName].payment_methods.groups;
		return availablePaymentMethods(paymentMethods, isConsortiumMember).map((paymentMethod) => {
			const option: IMuiSelectOption = { id: paymentMethod, label: I18n.asset.enums.EPaymentMethod[paymentMethod] };
			return option;
		});
	}, [productSelected, isConsortiumMember]);

	const isDemembrementType = subscriptionProductTypeSelected !== ESubscriptionProductType.pleine_propriete;

	const demembrementRatio = useMemo(() => {
		if (!isDemembrementType) return 1;
		if (demembrementType === EDemembrementType.viager) {
			if (viagerAge === null) return 1;
			return getViagerRatio(viagerAge, subscriptionProductTypeSelected) ?? 1;
		}
		if (!demembrementDuration || !productSelected) return 1;
		const key = productSelected.demembrementKeys?.find((k) => k.duration === demembrementDuration);
		if (!key) return 1;
		return subscriptionProductTypeSelected === ESubscriptionProductType.usufruit ? key.usufruitRatio : key.nueProprieteRatio;
	}, [isDemembrementType, demembrementType, viagerAge, demembrementDuration, productSelected, subscriptionProductTypeSelected]);

	const setProductInSubscription = useCallback(() => {
		if (!productSelected || !paymentMethodSelected) return;

		const productToAdd: IProductSelected = {
			product: productSelected,
			dto: ProductSubscriptionCreateResource.hydrate<ProductSubscriptionCreateResource>({
				productSheetId: productSelected.id,
				quantity: +amountSelected,
				paymentMethod: paymentMethodSelected,
				subscriberId: subscriberId,
				subscriptionProductType: subscriptionProductTypeSelected,
				demembrementDuration: demembrementDuration ?? undefined,
				demembrementType,
				viagerAge: viagerAge ?? undefined,
				contrepartieNom: contrepartieChoice === "avec" ? contrepartieNom ?? undefined : undefined,
			}),
		};

		setProductsInSubscription([...productsInSubscription, productToAdd]);
		setProductSelected(null);
		setPaymentMethodSelected(null);
		setPaymentMethodHasError(false);
		setDemembrementDuration(null);
		setContrepartieChoice(null);
		setContrepartieNom(null);
		if (isOpen) close();
	}, [
		productSelected,
		paymentMethodSelected,
		amountSelected,
		subscriberId,
		subscriptionProductTypeSelected,
		demembrementDuration,
		demembrementType,
		viagerAge,
		contrepartieChoice,
		contrepartieNom,
		setProductsInSubscription,
		productsInSubscription,
		isOpen,
		close,
	]);

	const getMinNumberOfShares = useCallback(() => {
		if (!productSelected?.minNumberOfShares) return 1;
		return productSelected?.minNumberOfShares;
	}, [productSelected?.minNumberOfShares]);

	const addProductToSubscription = useCallback(() => {
		if (!productSelected) return console.warn("No product selected");
		if (!paymentMethodSelected) {
			console.warn("No payment method selected");
			return setPaymentMethodHasError(true);
		}
		if (isDemembrementType && demembrementType === EDemembrementType.temporaire && !demembrementDuration) return console.warn("No demembrement duration selected");
		if (isDemembrementType && demembrementType === EDemembrementType.viager && viagerAge === null) return console.warn("No viager age provided");
		if (isDemembrementType && contrepartieChoice === null) return console.warn("No contrepartie choice made");
		if (isDemembrementType && contrepartieChoice === "avec" && !contrepartieNom) return console.warn("No contrepartie nom provided");
		if (amountSelected < getMinNumberOfShares()) return open();

		// Get client age from his birthdate compared day to day
		const age = calculateAge(new Date(userToSubscribe?.birthDate ?? ""), new Date());
		if (age >= 85 && productSelected.product?.referenceName === EAvailableProducts.kyaneos_pierre) return open85YearsModal();
		setProductInSubscription();
	}, [
		productSelected,
		paymentMethodSelected,
		isDemembrementType,
		demembrementDuration,
		demembrementType,
		viagerAge,
		contrepartieChoice,
		contrepartieNom,
		amountSelected,
		getMinNumberOfShares,
		open,
		userToSubscribe?.birthDate,
		open85YearsModal,
		setProductInSubscription,
	]);

	const onProductSelectedChange = useCallback(
		(values: Record<string, string | number> | null) => {
			if (!values) return setProductSelected(null);
			const selectedProduct = products.find((product) => product.id === values["id"]);
			if (!selectedProduct) return setProductSelected(null);
			setProductSelected(selectedProduct);
		},
		[products],
	);

	const onAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.value) return setAmountSelected(1);
		const value = parseInt(e.target.value);
		setAmountSelected(value);
	}, []);

	const enrichedProducts: IRowProps[] = useMemo(() => {
		return [
			{
				rowKey: "todo",
				subscriptionProductType: (
					<MuiSelectInputElement
						name="subscriptionProductType"
						options={subscriptionProductTypeOptions}
						onChange={(_event, values) => {
							const type = values && (values["id"] as ESubscriptionProductType);
							if (type) setSubscriptionProductTypeSelected(type);
						}}
						defaultValue={{ id: subscriptionProductTypeSelected, label: I18n.asset.enums.ESubscriptionProductType[subscriptionProductTypeSelected] }}
						disabled={!!lockedType}
					/>
				),
				name: (
					<MuiSelectInputElement
						name="products"
						options={productsAvailable
							.sort((productA, productB) => productA.name.localeCompare(productB.name))
							.map((product) => {
								const option: IMuiSelectOption = { id: product.id, label: product.name };
								return option;
							})}
						onChange={(_event, values) => {
							onProductSelectedChange(values);
						}}
						defaultValue={productSelected ? { id: productSelected.id, label: productSelected.name } : null}
					/>
				),
				paymentMethod: (
					<MuiSelectInputElement
						name={"paymentMethods"}
						options={paymentMethods}
						onChange={(_event, values) => {
							const paymentMethod = values && (values["id"] as EPaymentMethod);
							setPaymentMethodSelected(paymentMethod);
						}}
						style={paymentMethodHasError ? { borderColor: "#EF4444", border: "1px solid #EF4444", boxSizing: "border-box" } : {}}
						defaultValue={paymentMethodSelected ? { id: paymentMethodSelected, label: I18n.asset.enums.EPaymentMethod[paymentMethodSelected] ?? "" } : null}
					/>
				),
				price: productSelected?.price ?? "0",
				amount: <InputElement name="price" type={EInputType.NUMBER} onChange={onAmountChange} defaultValue={amountSelected} min={1} />,
				calculatedPrice: +amountSelected * (productSelected?.price ?? 0) * demembrementRatio,
				addToCart: (
					<Button
						onClick={addProductToSubscription}
						disabled={
							isDemembrementType &&
							((demembrementType === EDemembrementType.temporaire ? !demembrementDuration : viagerAge === null) ||
								contrepartieChoice === null ||
								(contrepartieChoice === "avec" && !contrepartieNom))
						}>
						{I18n.asset.common.add}
						<PlusIcon />
					</Button>
				),
			},
		];
	}, [
		subscriptionProductTypeOptions,
		subscriptionProductTypeSelected,
		lockedType,
		productsAvailable,
		productSelected,
		amountSelected,
		paymentMethodSelected,
		addProductToSubscription,
		onProductSelectedChange,
		paymentMethods,
		onAmountChange,
		paymentMethodHasError,
		isDemembrementType,
		demembrementDuration,
		demembrementType,
		viagerAge,
		contrepartieChoice,
		contrepartieNom,
		demembrementRatio,
	]);

	return (
		<>
			<Table columnsHead={productSelectorColumsHead} rows={enrichedProducts} placeholderSearchbar="Chercher un produit" searchbarDisabled onNext={null} />
			{isDemembrementType && productSelected && (
				<DemembrementConfigTable
					product={productSelected}
					subscriptionProductType={subscriptionProductTypeSelected}
					demembrementType={demembrementType}
					onDemembrementTypeChange={setDemembrementType}
					demembrementDuration={demembrementDuration}
					onDurationChange={setDemembrementDuration}
					viagerAge={viagerAge}
					onViagerAgeChange={setViagerAge}
					contrepartieChoice={contrepartieChoice}
					onContrepartieChoiceChange={setContrepartieChoice}
					contrepartieNom={contrepartieNom}
					onContrepartieNomChange={setContrepartieNom}
				/>
			)}
			<DemembrementWarnings
				isDemembrementType={isDemembrementType}
				productSelected={!!productSelected}
				contrepartieChoice={contrepartieChoice}
				subscriptionProductTypeSelected={subscriptionProductTypeSelected}
			/>
			<Modal isOpen={isOpen} onClose={close} className={classes["modal"]}>
				<Typography typo={ITypo.H1}>{I18n.asset.common.warning}</Typography>
				<Typography typo={ITypo.P_MEDIUM_BOLD}>
					{I18n.trslt(I18n.asset.pages.subscriptions.select_products.modal.text, { minimumShare: getMinNumberOfShares().toString() })}
				</Typography>

				<div className={classes["button-container"]}>
					<Button onClick={close} variant={EButtonVariant.OUTLINED}>
						{I18n.asset.common.cancel}
					</Button>
					<Button onClick={setProductInSubscription}>{I18n.asset.pages.subscriptions.select_products.modal.button}</Button>
				</div>
			</Modal>
			<Modal isOpen={is85YearsModalOpened} onClose={close85YearsModal} className={classes["modal"]}>
				<Typography typo={ITypo.H1}>{I18n.asset.common.warning}</Typography>
				<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.trslt(I18n.asset.pages.subscriptions.select_products.modal85years.text)}</Typography>

				<div className={classes["button-container"]}>
					<Button onClick={redirectToHome} variant={EButtonVariant.OUTLINED}>
						{I18n.asset.common.back_to_home}
					</Button>
					<Button onClick={close85YearsModal}>{I18n.asset.pages.subscriptions.select_products.modal85years.button}</Button>
				</div>
			</Modal>
		</>
	);
}
